import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import HeaderBGEnum from './HeaderBGEnum';

export type HeaderContextType = {
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  mobileMenuActiveItem: string | null;
  toggleMobileMenuActiveItem: (id: string) => void;
  hasScrolled: boolean;
  bgHeader: HeaderBGEnum;
};

const HeaderContext = createContext<HeaderContextType>({
  isMobileMenuOpen: false,
  toggleMobileMenu: () => {},
  mobileMenuActiveItem: null,
  toggleMobileMenuActiveItem: () => {},
  hasScrolled: false,
  bgHeader: HeaderBGEnum.Light,
});

export default HeaderContext;

interface HeaderContextProviderProps {
  children: React.ReactNode;
  bgHeader: HeaderBGEnum;
}

export function HeaderContextProvider({ children, bgHeader = HeaderBGEnum.Light }: HeaderContextProviderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuActiveItem, setMobileMenuActiveItem] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(!isMobileMenuOpen), [isMobileMenuOpen]);

  const toggleMobileMenuActiveItem = useCallback(
    (id: string) => {
      if (id === mobileMenuActiveItem) return setMobileMenuActiveItem(null);

      return setMobileMenuActiveItem(id);
    },
    [mobileMenuActiveItem],
  );

  useEffect(() => {
    const { scrollY } = window;

    if (scrollY > 0) {
      setHasScrolled(true);
    }
  }, []);

  const onScroll = useCallback(() => {
    const { scrollY } = window;

    if ((hasScrolled && !scrollY) || (!hasScrolled && scrollY)) {
      setHasScrolled(window.scrollY !== 0);
    }
  }, [hasScrolled]);

  useEffect(() => {
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const contextValuesWrapper = useMemo(
    () => ({
      isMobileMenuOpen,
      toggleMobileMenu,
      mobileMenuActiveItem,
      toggleMobileMenuActiveItem,
      hasScrolled,
      bgHeader,
    }),
    [isMobileMenuOpen, toggleMobileMenu, mobileMenuActiveItem, toggleMobileMenuActiveItem, hasScrolled, bgHeader],
  );

  return <HeaderContext.Provider value={contextValuesWrapper}>{children}</HeaderContext.Provider>;
}
